import { sanitizeAeroPressGrind } from "@client-shared/utilities/productSyncUtilities";
import { VendorRegionAllBagSizesConfig, VendorRegionAllGrindTypesConfig } from "@shared/index";
import * as T from "@shared/types";
import { omit } from "lodash";
import { v4 as generateUUID } from "uuid";
import { REQUIRED_GRIND } from "../../../lib/constants";
import { detectAllowedValuesFromString, detectedAllowedGrindsFromString, htmlToPlainText } from "../../../utilities/textUtilities";

//
export const setIsLocalProduct = (isLocalProduct: boolean | undefined) => {
  if (isLocalProduct === undefined) {
    return false;
  }
  return isLocalProduct;
};

export const setHasValidDetectedBagSize = (
  bagSize: string | undefined,
  mapDetectedBagSizeTo: string | undefined,
  validBagSizes?: string[]
) => {
  if (!bagSize) return undefined;
  if (mapDetectedBagSizeTo) return true;
  return validBagSizes?.includes(bagSize) ?? false;
};

type GrindType = string | string[] | undefined;

export const setHasValidDetectedGrindType = (validGrindTypes: string[], grind: GrindType, mapDetectedGrindTo: string | undefined) => {
  if (!grind) return false;
  if (mapDetectedGrindTo) return true;
  if (typeof grind === "string") {
    return validGrindTypes?.includes(grind);
  }
  return grind?.some((grind) => validGrindTypes?.includes(grind)) ?? false;
};

// checks if a grind type should be mapped to a different grind type
export const getMappedGrindType = (defaultGrindType: string, grindTypeMappings: VendorRegionAllGrindTypesConfig["grindTypeMapping"]) => {
  const mappableGrindTypes = Object.keys(grindTypeMappings ?? []);
  for (const mappableGrindTypeKey of mappableGrindTypes) {
    if (grindTypeMappings?.[mappableGrindTypeKey]?.includes(defaultGrindType)) {
      return mappableGrindTypeKey;
    }
  }
  return undefined;
};

export const setMapDetectedGrindTo = (
  grindType: string | T.FormVariant["grind"],
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig
): string | undefined => {
  let mapDetectedGrindTo: string | undefined = undefined;
  if (typeof grindType === "string" && allGrindTypesConfig?.grindTypeMapping) {
    mapDetectedGrindTo = getMappedGrindType(grindType, allGrindTypesConfig?.grindTypeMapping ?? []);
  }
  return mapDetectedGrindTo;
};

export const getMappedBagSize = (defaultBagSize: string, bagSizeMappings: VendorRegionAllBagSizesConfig["bagSizeMapping"]) => {
  const mappableBagSizes = Object.keys(bagSizeMappings ?? []);
  let mappedBagSize: undefined | string = undefined;
  mappableBagSizes.forEach((mappableBagSizeKey) => {
    const matchingBag = bagSizeMappings?.[mappableBagSizeKey]?.includes(defaultBagSize);
    if (mappedBagSize ?? matchingBag) {
      mappedBagSize = mappableBagSizeKey;
    }
  });
  return mappedBagSize;
};

export const setMapDetectedBagSizeTo = (
  bagSize: string | undefined,
  allBagSizesConfig?: VendorRegionAllBagSizesConfig
): string | undefined => {
  if (!bagSize || !allBagSizesConfig || typeof bagSize !== "string") return;

  let mapDetectedBagSizeTo: undefined | string = undefined;

  if (typeof bagSize === "string") {
    mapDetectedBagSizeTo = getMappedBagSize(bagSize, allBagSizesConfig?.bagSizeMapping ?? {});
  }
  return mapDetectedBagSizeTo;
};

export const setDetectedBagSize = (
  variant: T.Variant,
  productConfig: T.ProductConfig,
  allBagSizesConfig?: VendorRegionAllBagSizesConfig
) => {
  const detectedBagSize =
    // first check against the valid bag sizes for the product
    detectAllowedValuesFromString(variant.title, productConfig?.bagSize ?? []) ??
    // if no productConfig found, check against all known bag sizes
    detectAllowedValuesFromString(variant.title, allBagSizesConfig?.bagSizes ?? []);

  return detectedBagSize;
};

export const mapDetectedGrindsToTranslations = (
  detectedGrindTypes: string[],
  localisedProductConfig: T.LocalisedProductConfig
): string[] => {
  return detectedGrindTypes.flatMap((detectedGrind) => {
    const translatedGrinds = localisedProductConfig.grind
      .filter((option) => option.label.includes(detectedGrind))
      .map((option) => option.value)
      .filter(Boolean);
    return translatedGrinds.length > 0 ? translatedGrinds : [detectedGrind];
  });
};

export const setDetectedGrindType = (
  productGrind: T.Product["grind"],
  variant: T.Variant,
  productConfig: T.ProductConfig,
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig,
  localisedProductConfig?: T.LocalisedProductConfig,
  localisedGrinds?: string[]
) => {
  if (productGrind) {
    return productGrind;
  }

  const allGrinds = new Set([...(localisedGrinds ?? []), ...(allGrindTypesConfig?.grindTypes ?? []), ...(productConfig?.grind ?? [])]);
  const detectedGrindTypes = detectedAllowedGrindsFromString(variant.title, [...allGrinds]);
  
  if (detectedGrindTypes && localisedProductConfig?.grind) {
    return mapDetectedGrindsToTranslations(detectedGrindTypes, localisedProductConfig);
  }
  
  return detectedGrindTypes;
};

export const setLocaliseGrindTypes = (localisedProductConfig?: T.LocalisedProductConfig) => {
  return localisedProductConfig?.grind.map((option) => option.label).flatMap((grind) => grind.split(" / ")) ?? [];
};

export const setHasDetectedGrindType = (grindType: string | string[] | undefined) => {
  if (!grindType) return undefined;
  return Array.isArray(grindType) ? grindType?.length > 0 : typeof grindType === "string";
};

export const setHasDetectedBagSize: (bagSize: string | undefined) => boolean = Boolean;

export const setGrindType = (detectedGrindType: string | string[] | undefined) => {
  if (!detectedGrindType || (Array.isArray(detectedGrindType) && detectedGrindType.length === 0)) return [REQUIRED_GRIND];
  if (typeof detectedGrindType === "string") {
    return [detectedGrindType];
  }
  return detectedGrindType;
};

export const setHasTermOverrideForLargeBag = (
  regionLargeBagSize: string[] | undefined,
  detectedBagSize?: string,
  mapDetectedBagSizeTo?: string
) => {
  if (!regionLargeBagSize || !detectedBagSize) return false;
  const bagSize = mapDetectedBagSizeTo ?? detectedBagSize;
  return regionLargeBagSize.includes(bagSize);
};

export const setTermOverrideForLargeBag = (
  regionLargeBagSize: string[] | undefined,
  detectedBagSize?: string,
  mapDetectedBagSizeTo?: string,
  term?: string
) => {
  const hasTermOverrideForLargeBag = setHasTermOverrideForLargeBag(regionLargeBagSize, detectedBagSize, mapDetectedBagSizeTo);
  if (hasTermOverrideForLargeBag) {
    return "Short";
  }
  return term;
};

export const getVariantGrindDetails = ({
  productGrind,
  variant,
  productConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  localiseGrinds,
}: {
  productGrind: T.Product["grind"];
  variant: T.Variant;
  productConfig: T.ProductConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  localiseGrinds: string[];
}) => {
  const detectedGrindTypes = setDetectedGrindType(
    productGrind,
    variant,
    productConfig,
    allGrindTypesConfig,
    localisedProductConfig,
    localiseGrinds
  );
  const detectedGrindInfo: T.DetectedGrindInfo[] = detectedGrindTypes.map((grind) => {
    const mapDetectedGrindTo = setMapDetectedGrindTo(grind, allGrindTypesConfig);
    const isValidGrind = setHasValidDetectedGrindType(productConfig.grind ?? [], grind, mapDetectedGrindTo);
    return {
      grind,
      mapDetectedGrindTo,
      isValidGrind,
    };
  });

  const hasValidDetectedGrindType = detectedGrindInfo.some((info) => info.isValidGrind);
  const hasDetectedGrindType = setHasDetectedGrindType(detectedGrindTypes);
  const grind = setGrindType(detectedGrindTypes);

  return { grind, detectedGrindInfo, hasDetectedGrindType, hasValidDetectedGrindType };
};

// maps variants to a FormVariant to be used in the form
export const mapVariantToFormVariant = ({
  productGrind,
  variant,
  productConfig,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  localiseGrinds,
  isLocalProduct,
  regionLargeBagSize,
}: {
  productGrind: T.Product["grind"];
  variant: T.Variant;
  productConfig: T.ProductConfig;
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  localiseGrinds: string[];
  isLocalProduct?: boolean;
  regionLargeBagSize?: string[];
}) => {
  const detectedBagSize = setDetectedBagSize(variant, productConfig, allBagSizesConfig);
  const { grind, hasDetectedGrindType, hasValidDetectedGrindType, detectedGrindInfo } = getVariantGrindDetails({
    productGrind,
    variant,
    productConfig,
    allGrindTypesConfig,
    localisedProductConfig,
    localiseGrinds,
  });

  const hasDetectedBagSize = setHasDetectedBagSize(detectedBagSize);
  const mapDetectedBagSizeTo = setMapDetectedBagSizeTo(detectedBagSize, allBagSizesConfig);
  const hasValidDetectedBagSize = setHasValidDetectedBagSize(detectedBagSize, mapDetectedBagSizeTo, productConfig.bagSize);
  const term = setTermOverrideForLargeBag(regionLargeBagSize, detectedBagSize, mapDetectedBagSizeTo, variant.term);
  if (isLocalProduct) {
    return {
      ...variant,
      bagSize: detectedBagSize,
      grind,
    };
  }
  return {
    ...variant,
    term: term,
    bagSize: detectedBagSize,
    mapDetectedBagSizeTo: mapDetectedBagSizeTo,
    grind,
    hasDetectedGrindType: hasDetectedGrindType,
    hasDetectedBagSize: hasDetectedBagSize,
    hasValidDetectedBagSize: hasValidDetectedBagSize,
    hasValidDetectedGrindType: hasValidDetectedGrindType,
    detectedGrindInfo: detectedGrindInfo,
  } as T.FormVariant;
};

export const mapAllVariantsToFormVariants = ({
  variants,
  productConfig,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  isLocalProduct,
  localiseGrinds,
  productGrind,
  regionLargeBagSize,
}: {
  variants: T.Variant[] | undefined;
  productConfig: T.ProductConfig;
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  isLocalProduct: boolean;
  localiseGrinds: string[];
  productGrind: T.Product["grind"];
  regionLargeBagSize?: string[];
}) => {
  if (!variants || variants.length === 0) return [];

  const mappedVariants = variants.map((variant) =>
    mapVariantToFormVariant({
      productGrind: productGrind,
      variant: variant,
      productConfig: productConfig,
      allBagSizesConfig: allBagSizesConfig,
      allGrindTypesConfig: allGrindTypesConfig,
      localisedProductConfig: localisedProductConfig,
      localiseGrinds: localiseGrinds,
      isLocalProduct: isLocalProduct,
      regionLargeBagSize: regionLargeBagSize,
    })
  ) as T.FormVariant[];

  if (!isLocalProduct) {
    checkVariantsWithSameBagSize(mappedVariants);
  }
  return mappedVariants;
};

export const checkVariantsWithSameBagSize = (mappedVariants: T.FormVariant[]) => {
  mappedVariants.forEach((mappedVariant) => {
    mappedVariant.isVariantHasSameBagSize = mappedVariants.some(
      (variant) =>
        variant.id !== mappedVariant.id &&
        (variant.mapDetectedBagSizeTo !== undefined || variant.bagSize !== undefined) &&
        (variant.mapDetectedBagSizeTo ?? variant.bagSize) === (mappedVariant.mapDetectedBagSizeTo ?? mappedVariant.bagSize)
    );
  });
};

export const mapProductToFormProduct = ({
  product,
  productConfig,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  isLocalProduct,
  localiseGrinds,
  regionLargeBagSize,
}: {
  product: T.Product;
  productConfig: T.ProductConfig;
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  isLocalProduct: boolean;
  localiseGrinds: string[];
  regionLargeBagSize?: string[];
}) => {
  return {
    ...product,
    variants: mapAllVariantsToFormVariants({
      variants: product.variants,
      productConfig: productConfig,
      allBagSizesConfig: allBagSizesConfig,
      allGrindTypesConfig: allGrindTypesConfig,
      localisedProductConfig: localisedProductConfig,
      isLocalProduct: isLocalProduct,
      localiseGrinds: localiseGrinds,
      productGrind: product?.grind,
      regionLargeBagSize: regionLargeBagSize,
    }),
  } as T.FormProduct;
};

// takes local and remote products from the API and returns it as a FormProduct for the form to use

export interface MapProductsToFormProductsArgs {
  products: T.Product[];
  productConfig: T.ProductConfig;
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  isLocalProduct: boolean;
  regionLargeBagSize?: string[];
}

export const mapProductsToFormProducts = ({
  products,
  productConfig,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  isLocalProduct,
  regionLargeBagSize,
}: MapProductsToFormProductsArgs): T.FormProduct[] => {
  const localiseGrinds = setLocaliseGrindTypes(localisedProductConfig);

  return products.map((product) => {
    return mapProductToFormProduct({
      product: product,
      productConfig: productConfig,
      allBagSizesConfig: allBagSizesConfig,
      allGrindTypesConfig: allGrindTypesConfig,
      localisedProductConfig: localisedProductConfig,
      isLocalProduct: isLocalProduct,
      localiseGrinds: localiseGrinds,
      regionLargeBagSize: regionLargeBagSize,
    });
  });
};

const getLocalVariant = (variant: T.FormVariant, localVariants?: T.FormVariant[], connections?: T.ProductConnection[]) => {
  if (!localVariants?.length || !connections?.length) return undefined;

  const connection = connections.find((connection) => connection.targetVariantId === variant.id);
  if (!connection) return undefined;

  const localVariant = localVariants.find((localVariant) => localVariant.id === connection.sourceObjectId);
  if (!localVariant) return undefined;

  return { ...localVariant, grind: connection?.grindType ? [connection.grindType] : variant.grind };
};

const mapLocalGrindToRemoteGrind = (variant: T.FormVariant, localVariant?: T.FormVariant) => {
  if (!localVariant) return undefined;
  return localVariant?.grind ?? undefined;
};

const setIsImportedLocalVariant: (localVariant?: T.FormVariant) => boolean = Boolean;
// takes a remote FormProduct and gets the data from the local product and updates the missing data
export const mapToLocalProduct = ({
  remoteProduct,
  localProducts,
  connections,
  productConfig,
  roasterNoteMaxLength,
  titleMaxLength,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  regionLargeBagSize,
}: {
  remoteProduct: T.FormProduct;
  localProducts: T.Product[];
  connections: T.ProductConnection[];
  productConfig: T.ProductConfig;
  roasterNoteMaxLength?: number;
  titleMaxLength?: number;
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  regionLargeBagSize?: string[];
}): T.FormProduct => {
  const localToFormProducts = mapProductsToFormProducts({
    products: localProducts,
    productConfig: productConfig,
    allBagSizesConfig: allBagSizesConfig,
    allGrindTypesConfig: allGrindTypesConfig,
    localisedProductConfig: localisedProductConfig,
    isLocalProduct: true,
    regionLargeBagSize: regionLargeBagSize,
  });

  const localVariants = localToFormProducts
    .filter((product) => product.variants?.length)
    .flatMap((product) => product.variants);

  const localProduct = localToFormProducts[0];
  if (roasterNoteMaxLength && localProduct?.roasterNotes && localProduct.roasterNotes.length > roasterNoteMaxLength) {
    localProduct.roasterNotes = localProduct?.roasterNotes
      ?.split("")
      .slice(0, roasterNoteMaxLength - 1)
      .join("");
  }
  if (titleMaxLength && localProduct?.title?.length) {
    localProduct.title = localProduct?.title
      ?.split("")
      .slice(0, titleMaxLength - 1)
      .join("");
  }
  return {
    ...(localProduct as T.FormProduct),
    id: remoteProduct?.id,
    serviceId: remoteProduct?.serviceId,
    title: remoteProduct.title,
    localTitle: localProduct?.title,
    uuid: remoteProduct.uuid,
    connections: connections,
    imageUploads: localProduct?.images?.[0]
      ? [
          {
            type: "url",
            url: localProduct?.images?.[0].src,
          },
        ]
      : null,
    localVariants: localVariants.length > 0 ? localVariants : undefined,
    variants: remoteProduct.variants.map((variant) => {
        const localVariant = getLocalVariant(variant, localVariants, connections);
        return {
          ...variant,
          localGrindOverride: mapLocalGrindToRemoteGrind(variant, localVariant),
          isImportedLocalVariant: setIsImportedLocalVariant(localVariant),
        };
    }),
  };
};

// finds the local product and connection that matches the remote product and returns them
export const matchToLocalProductsAndConnections = (
  product: T.FormProduct,
  connections: T.ProductConnection[] | undefined,
  localProducts: T.Product[] | undefined
) => {
  if (!connections || !localProducts) {
    return undefined;
  }
  const localProductsWithConnections = localProducts?.filter((localProduct) =>
    connections?.some((connection) => localProduct?.variants?.some((variant) => variant.id === connection.sourceObjectId))
  );

  const connectionsWithMatchingRemoteVariants = connections?.filter((connection) =>
    product.variants.some((variant) => variant.id === connection.targetVariantId)
  );

  const local = localProductsWithConnections?.filter((localProduct) =>
    connectionsWithMatchingRemoteVariants?.some((connection) =>
      localProduct?.variants?.some((variant) => variant.id === connection.sourceObjectId)
    )
  );

  if (local && local?.length > 0) {
    return { localProducts: local, connections: connectionsWithMatchingRemoteVariants };
  }
  return undefined;
};

// makes sure that the bagsize is the same as the picklist as sometimes SF returns lowercase or uppercase
export const sanitizeRemoteVariantBagSize = (variant: T.FormVariant, productConfig: T.ProductConfig) => {
  const detectedBagSize = detectAllowedValuesFromString(variant.title, productConfig.bagSize ?? []);
  if (!detectedBagSize) return;
  variant.bagSize = detectedBagSize;
};

// updates the remote product with the imported variants
export const updateToWithImportedVariants = (product: T.FormProduct, importedProducts: T.Product[], productConfig: T.ProductConfig) => {
  const importedVariants = importedProducts.flatMap((product) => product.variants);

  if (importedVariants.length === 0) {
    return product;
  }

  product.variants.forEach((variant) => {
    variant.isImportedRemote = false;

    const matchingImportedProducts = importedProducts?.filter((product) =>
      product.variants?.some((importedVariant) => importedVariant.id === variant.id)
    );

    sanitizeRemoteVariantBagSize(variant, productConfig);

    if (matchingImportedProducts.length === 0) {
      return;
    }

    const latestImportedProduct = matchingImportedProducts?.pop();
    if (latestImportedProduct && latestImportedProduct?.status === "Rejected") {
      variant.serviceId = latestImportedProduct.serviceId;
    }

    const importedVariant = latestImportedProduct?.variants?.find((importedVariant) => importedVariant.id === variant.id);
    if (!importedVariant) {
      return;
    }

    // Imported grinds of a single variant mapped to multiple grind types.
    const importedGrinds =
      latestImportedProduct?.variants
        ?.filter((importedVariant) => importedVariant.id === variant.id)
        .flatMap((variant) => {
          const grinds = variant.grind ?? [];
          const sanitizedGrinds = grinds.map((grind) => sanitizeAeroPressGrind(grind));
          return sanitizedGrinds;
        }) ?? [];

    const { maxBagsShippedPerWeek, inventoryQuantity, price, wholesalePrice, term, startDate, webPrice, endDate } = importedVariant;

    Object.assign(variant, {
      status: latestImportedProduct?.status,
      isImportedRemote: true,
      weeklyInventory: maxBagsShippedPerWeek,
      totalMaxShortTermInventory: inventoryQuantity,
      price,
      wholesalePrice,
      term,
      startDate,
      endDate,
      webPrice,
      grind: importedGrinds,
    });

    // When variant bag size is not detected and imported by selecting bag size from dropdown
    // assign the imported bag size to the variant.
    if (!variant.hasDetectedBagSize && importedVariant?.bagSize) {
      variant.bagSize = importedVariant.bagSize;
    }
  });

  const importedProduct = updateVariantFromImportedSameBagSize(product, importedProducts);
  return importedProduct;
};

export const updateVariantFromImportedSameBagSize = (product: T.FormProduct, importedProducts: T.Product[]): T.FormProduct => {
  const importedVariants = product.variants.filter((variant) => variant.isImportedRemote);
  if (importedVariants.length === 0) {
    return product;
  }

  const importedProduct = importedProducts.find((importedProduct) =>
    importedProduct.variants?.some((variant) => variant.id === importedVariants[0]?.id)
  );

  return {
    ...product,
    ...omit(importedProduct, ["serviceId", "status", "id"]),
    title: product.title,
    localTitle: importedProduct?.title,
    variants: product.variants.map((variant) => {
      const variantByBagSize = importedVariants.find(
        (importedVariant) => importedVariant.bagSize === (variant.mapDetectedBagSizeTo ?? variant.bagSize)
      );
      const isVariantImported = variant.isImportedRemote && variant.status !== "Rejected";

      if (!variantByBagSize || isVariantImported || variantByBagSize?.status === "Rejected") {
        return variant;
      }

      const { price, term, wholesalePrice, weeklyInventory, totalMaxShortTermInventory, webPrice } = variantByBagSize;
      return {
        ...variant,
        price,
        term,
        wholesalePrice,
        weeklyInventory,
        totalMaxShortTermInventory,
        webPrice,
        isBagSizeVariantImported: true,
      };
    }),
  };
};

// maps all the products from the API to a FormProduct
// updates the product with the imported variants
// matches the product to the local product and connections
// returns the updated product
export const mapAllProducts = ({
  products,
  productConfig,
  importedProducts,
  localProducts,
  connections,
  allBagSizesConfig,
  allGrindTypesConfig,
  localisedProductConfig,
  roasterNoteMaxLength,
  titleMaxLength,
  regionLargeBagSize,
}: {
  products: T.Product[];
  productConfig: T.ProductConfig;
  importedProducts?: T.Product[];
  localProducts?: T.Product[];
  connections?: T.ProductConnection[];
  allBagSizesConfig?: VendorRegionAllBagSizesConfig;
  allGrindTypesConfig?: VendorRegionAllGrindTypesConfig;
  localisedProductConfig?: T.LocalisedProductConfig;
  roasterNoteMaxLength?: number;
  titleMaxLength?: number;
  regionLargeBagSize?: string[];
}) => {
  const formProducts = mapProductsToFormProducts({
    products: products,
    productConfig: productConfig,
    allBagSizesConfig: allBagSizesConfig,
    allGrindTypesConfig: allGrindTypesConfig,
    localisedProductConfig: localisedProductConfig,
    isLocalProduct: false,
    regionLargeBagSize,
  });
  return formProducts.map((product) => {
    product.uuid = generateUUID();
    product.description = htmlToPlainText(product.description);
    // update the product with the imported variants (SALESFORCE)
    const updatedProductWithServiceData = updateToWithImportedVariants(product, importedProducts ?? [], productConfig);
    // match the product to the local product and connections
    const matchedLocalProductsAndConnections = matchToLocalProductsAndConnections(
      updatedProductWithServiceData,
      connections,
      localProducts
    );
    // if no local product and connection found, return the updated product
    if (!matchedLocalProductsAndConnections) {
      return { ...updatedProductWithServiceData };
    }
    // return the updated product with the local product and connection data
    return mapToLocalProduct({
      remoteProduct: updatedProductWithServiceData,
      localProducts: matchedLocalProductsAndConnections?.localProducts,
      connections: matchedLocalProductsAndConnections?.connections,
      productConfig: productConfig,
      roasterNoteMaxLength: roasterNoteMaxLength,
      titleMaxLength: titleMaxLength,
      allBagSizesConfig: allBagSizesConfig,
      allGrindTypesConfig: allGrindTypesConfig,
      localisedProductConfig: localisedProductConfig,
    });
  });
};
